/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getInheritedSchedules} from '@ion/core/src/services/Maintenance';
import {AssetType, MaintenanceSchedule} from '@ion/core/src/types/entities';
import {isEqual} from '@ion/core/src/utils/StringUtil';
import Dropdown, {Options} from '../components/menus/Dropdown';

/**
 * Defines the prop required and optional prop types supported by this component.
 */
export type MaintenanceScheduleChooserProps = {
  accountId: number;
  assetType?: string;
  assetCategory?: string;
  schedule?: MaintenanceSchedule;
  onChange?: (schedule: MaintenanceSchedule) => void;
  readOnly?: boolean;
  required?: boolean;
  includeNone?: boolean
  validationError?: string;
  classes?: string;
  defaultValue?: string;
}

/**
 * Generates the Maintenance Schedules based on
 * the optional filters (asset type and category).
 */
const getOptions = (
  schedules: MaintenanceSchedule[], type: string, category: string
): MaintenanceSchedule[] => {
  if (schedules?.length) {
    const filtered = schedules?.filter(({assetType, assetCategory}: MaintenanceSchedule) => {
      if (category || type !== AssetType.Default) {
        const categoryValid = category ? isEqual(assetCategory, category) || !assetCategory : true;
        const typeValid     = type ? isEqual(assetType, type) || !assetType : true;
        return categoryValid && typeValid;
      }
      return true;
    });
    return filtered;
  }
  return [];
};

/**
 * Generates the Maintenance Schedule Chooser dropdown labels and values based on
 * the provide schedules.
 */
const createOptions = (schedules: MaintenanceSchedule[]): Options[] => {
  if (schedules?.length) {
    return schedules.map(({scheduleId, name}: MaintenanceSchedule) => {
      return {value: scheduleId, label: name};
    }) as Options[];
  }
  return [];
};

/**
 * Provides a utility which filters a list of maintenance schedules for the
 * currently selected value.
 */
const getSelectedValue = (
  schedules: MaintenanceSchedule[],
  selected: string
): MaintenanceSchedule => {
  return schedules.find(({name}: MaintenanceSchedule) => name === selected);
};

// default styles used by the component when a classes prop is not provided ...
const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  }
}));

/**
 * @MaintenanceScheduleChooser
 *
 * The MaintenanceScheduleChooser component displays a list of maintenance schedules
 * based on the provided account, and allows for selecting a maintenance schedule
 * from the list for subsequent use. In addition, the account's maintenance schedules
 * can optionally be filtered by assetType and / or assetCategory.
 *
 * ```typescript
 *
 *     import {MaintenanceScheduleChooser} from '@ion/dls/src/editors/';
 *
 *     <MaintenanceScheduleChooser account={selectedAccount} onChange={setSchedule} />
 *
 *     // filter the list of schedules based on the given asset type and categories ...
 *     <MaintenanceScheduleChooser onChange={setSchedule}
 *       account={selectedAccount}
 *       assetType={selectedAssetType}
 *       assetCategory={selectedAssetCategory}
 *       />
 *
 *     // read-only mode ...
 *     <MaintenanceScheduleChooser readOnly
 *       account={selectedAccount}
 *       assetType={selectedAssetType}
 *       assetCategory={selectedAssetCategory}
 *       />
 *
 * ```
 *
 * @accountId        The id of the Account for which maintenance schedules are to be loaded.
 * @assetType        Optional, allows for providing an assetType on which the maintenance
 *                   schedules are to be filtered.
 * @assetCategory    Optional, allows for providing an assetCategory on which the
 *                   maintenance schedules are to be filtered.
 * @onChange         Callback prop which will be invoked with the selected maintenance
 *                   schedule.
 * @readOnly         When true, the component will be displayed in read-only mode;
 *                   that is, disabled. Defaults to false.
 * @required         When true, the required indicator will be displayed.
 * @validationError  When true, the validationError indicator will be displayed.
 * @classes          Optional classes to apply to the wrapping element.
 * @includeNone      When true (default), localized "None" option will be displayed.
 * @defaultValue     Default Value of the field
 */
export const MaintenanceScheduleChooser = ({
  accountId,
  assetType,
  assetCategory,
  schedule,
  onChange,
  readOnly,
  required,
  classes,
  validationError,
  includeNone = true,
  defaultValue
}: MaintenanceScheduleChooserProps) => {
  const _classes = useStyles();
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(accountId));
  const [selected, setSelected]   = useState<string>(schedule?.name || '');
  const [options, setOptions]     = useState<Options[]>([]);
  const hasMounted                = useHasMounted();

  useEffect(() => {
    if (defaultValue && !selected) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  // whenever the account changes, the maintenance schedules are reloaded ...
  useEffect(() => {
    accountId && loadSchedules(accountId);
  }, [accountId]);

  // whenever the asset type or categories change, we filter the schedules list ...
  useEffect(() => {
    if (schedules?.length) {
      customOptions(schedules);
    }
  }, [assetType, assetCategory]);

  // manages loading maintenance schedules based on the current accountId prop ...
  const loadSchedules = async function(accountId: number) {
    let results: MaintenanceSchedule[];
    setIsLoading(true);
    try {
      results = await getInheritedSchedules(accountId);
    } catch (err) {
      error(`Maintenance Schedules: Error occurred retrieving Maintenance Schedules for account ${accountId}`);
    } finally {
      if (hasMounted !== false) {
        setSchedules(results);
        customOptions(results);
        setIsLoading(false);
      }
    }
  };

  // manages value of maintenance schedule field based on selected accountId prop, asset type or catergory ...
  const customOptions = function(schedules: MaintenanceSchedule[]) {
    const filterOptions = getOptions(schedules, assetType, assetCategory);
    setOptions(createOptions(filterOptions));
    if (selected) {
      const selectedValue = getSelectedValue(filterOptions, selected);
      if (!selectedValue) {
        onChange?.(undefined);
        setSelected('');
      } else {
        onChange?.(getSelectedValue(schedules, selected));
      }
    }
  };

  return (
    <div className={classes || _classes.root}>
      <Dropdown includeNone={includeNone}
        displaySelectedLabel
        required={required}
        disabled={readOnly || isLoading}
        hasError={Boolean(validationError)}
        options={options}
        loading={isLoading}
        value={!isLoading ? selected : translate('common:states.loading')}
        fieldLabel={translate('common:editors.maintenanceSchedule.label')}
        placeholder={translate('common:editors.maintenanceSchedule.placeholder')}
        preferPlaceholder={!isLoading && !selected}
        onChange={onChange ? function({target}) {
          const name = target.value as unknown as string;
          setSelected(name);
          onChange(getSelectedValue(schedules, name));
        } : null} />
    </div>
  );
};
export default MaintenanceScheduleChooser;
